/**
 * 캐스트룸 도메인 타입
 *
 * 용어 정의서(DES_PRJ_용어정의서.md) 기준
 */

// ── 캐스트룸 상태 ────────────────────────────────────────
// waiting → ready → casting → closed

export type CastroomStatus = 'waiting' | 'ready' | 'casting' | 'closed'

// ── 시청자 상태 ──────────────────────────────────────────
// waiting → connected → receiving

export type ViewerStatus = 'waiting' | 'connected' | 'receiving'

// ── 캐스트룸 식별자 ──────────────────────────────────────
// 불투명 식별자: 내부 필드에 Id/Name 접미사 없이 도메인 이름만 사용.
// 구성 속성이 변경되어도 유틸리티 함수만 수정하면 사용처는 영향 없음.

export interface CastroomId {
  gcs: string        // 지상제어시스템
  drone: string      // 드론
  camera: string     // 카메라
}

// ── CastroomId 유틸리티 ─────────────────────────────────
// 생성, 비교, 직렬화를 한곳에서 관리하여 변경 영향을 차단한다.
// TCastroomId 바깥에서 개별 값을 다루는 변수명은 gcsName, droneName, cameraName을 사용한다.

/** 캐스트룸 식별자 생성 — 개별 값으로부터 만드는 유일한 진입점 */
export function createCastroomId(gcsName: string, droneName: string, cameraName: string): CastroomId {
  return { gcs: gcsName, drone: droneName, camera: cameraName }
}

/** 동일 캐스트룸 여부 비교 */
export function castroomIdEquals(a: CastroomId, b: CastroomId): boolean {
  return a.gcs === b.gcs && a.drone === b.drone && a.camera === b.camera
}

/** UI 표시용 문자열 (예: "KETI_GCS / drone01 / cam01") */
export function castroomIdToString(id: CastroomId): string {
  return `${id.gcs} / ${id.drone} / ${id.camera}`
}

/** Map 키/로그용 고유 키 (예: "KETI_GCS:drone01:cam01") */
export function castroomIdToKey(id: CastroomId): string {
  return `${id.gcs}:${id.drone}:${id.camera}`
}

// ── 송출 소스 ────────────────────────────────────────────

import type { VideoSourceType } from './video-source.types'

export interface CastingSource {
  castroomId: CastroomId
  rtspUrl?: string
  /**
   * 영상 소스 입력 유형. 캐스트룸 생성 시점에 확정되는 불변 분류값.
   * 구버전 caster 호환을 위해 선택 필드. 미지정 시 서버가 rtspUrl/mediaServerType
   * 조합으로 폴백 추정한다.
   */
  sourceType?: VideoSourceType
}

// ── 기존 상태값 → 신규 상태값 매핑 ──────────────────────

export const LEGACY_CASTROOM_STATUS: Record<string, CastroomStatus> = {
  'pre-created': 'waiting',
  'created': 'ready',
  'casting': 'casting',
}

export const LEGACY_VIEWER_STATUS: Record<string, ViewerStatus> = {
  'waiting': 'waiting',
  'joined': 'connected',
  'playing': 'receiving',
}
